import React, { useState } from 'react';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { Box, Button, Dialog, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { format } from 'date-fns';

import { KitOrder, OrderListQuery } from 'api/Responses/OrderResponses';
import { useLanguage } from 'context/AppContext';

import { ModalContent } from '../DeliveryPortalPage.styles';

interface FilterModalProps {
  isShown: boolean;
  onClose: () => void;
  setFilters: (filters: Omit<OrderListQuery, 'last_id'>) => void;
  filters: Omit<OrderListQuery, 'last_id'>;
}

const FilterModal: React.FC<FilterModalProps> = ({ isShown, onClose, setFilters, filters }) => {
  const [language] = useLanguage();
  const [dateFrom, setDateFrom] = useState<Date>(filters.date_from ? new Date(filters.date_from) : new Date());
  const [dateTo, setDateTo] = useState<Date>(filters.date_to ? new Date(filters.date_to) : new Date());
  const [status, setStatus] = useState<KitOrder['status']>(filters.status ? filters.status : 'UNCONFIRMED');

  const handleChangeDateFrom = (newValue: Date | null) => {
    if (newValue) {
      setDateFrom(newValue);
    }
  };

  const handleChangeDateTo = (newValue: Date | null) => {
    if (newValue) {
      setDateTo(newValue);
    }
  };

  const handleApplyFilters = () => {
    setFilters({
      status: status,
      date_from: format(dateFrom, 'yyyy-MM-dd'),
      date_to: format(dateTo, 'yyyy-MM-dd'),
    });
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  return (
    <Dialog open={isShown} fullWidth>
      <ModalContent>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography style={{ fontWeight: 'bold' }} align="center">
              {language.DeliveryPortal.FilterModal.heading}
            </Typography>
          </Grid>
          <Grid item>
            <Button onClick={onClose}>{language.DeliveryPortal.FilterModal.closeButton}</Button>
          </Grid>
        </Grid>

        <Box mb={4} />

        <Grid container alignItems="center">
          <Grid item>
            <InputLabel>{language.DeliveryPortal.FilterModal.status}</InputLabel>
          </Grid>
          <Box mr={2} />
          <Grid item>
            <Select
              label={language.DeliveryPortal.FilterModal.status}
              value={status}
              onChange={e => setStatus(e.target.value as KitOrder['status'])}
            >
              {language.DeliveryPortal.FilterModal.statusOptions.map(opt => (
                <MenuItem value={opt.value} key={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Box mb={4} />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container alignItems="center" sx={{ mb: 4 }}>
            <Grid item xs={12} sx={{ mb: 2 }}>
              <InputLabel>{language.DeliveryPortal.FilterModal.date}</InputLabel>
            </Grid>
            <Grid item xs={6}>
              <DesktopDatePicker
                label={language.DeliveryPortal.FilterModal.dateFrom}
                inputFormat="dd/MM/yyyy"
                value={dateFrom}
                onChange={handleChangeDateFrom}
                renderInput={(params: any) => <TextField {...params} />}
              />
            </Grid>
            <Grid item xs={6}>
              <DesktopDatePicker
                label={language.DeliveryPortal.FilterModal.dateTo}
                inputFormat="dd/MM/yyyy"
                value={dateTo}
                onChange={handleChangeDateTo}
                renderInput={(params: any) => <TextField {...params} />}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Grid container justifyContent="space-between">
          <Button variant="contained" onClick={handleClearFilters}>
            {language.DeliveryPortal.FilterModal.clearFiltersButton}
          </Button>
          <Button variant="contained" onClick={handleApplyFilters}>
            {language.DeliveryPortal.FilterModal.applyFiltersButton}
          </Button>
        </Grid>
      </ModalContent>
    </Dialog>
  );
};

export default FilterModal;
